import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useGetAllBankInfoQuery } from "../../../../store/admin/settings/societyBankSlice";
import { useCreateContributionCategoryMutation } from "../../../../store/admin/contribution";
import { useGetCurrentStaffProfileQuery } from "../../../../store/admin/staff";
import toast from "react-hot-toast";
import { Input } from "../../../../components/Ui/new-input";
import CustomSelect from "../../../../components/custom-select/custom-select";
import { customStyles } from "../../../../components/custom-select/custom-styles";

const CATEGORY_KIND_OPTIONS = [
  { label: "Savings (withdrawable)", value: "savings" },
  { label: "Shares (non-withdrawable)", value: "shares" },
  { label: "Other", value: "other" },
];

function withdrawableForKind(kind) {
  if (kind === "savings") return 1;
  if (kind === "shares") return 0;
  return null;
}

export default function CreateContributionModal({ handleClose }) {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.auth.userData);
  const { data: profileData } = useGetCurrentStaffProfileQuery();

  const currentContext = profileData || userData;
  const currentBranch = currentContext?.current_branch;
  const activeBranchId = useMemo(
    () =>
      currentBranch?.id ??
      userData?.current_branch?.id ??
      userData?.active_branch_id,
    [currentBranch?.id, userData?.current_branch?.id, userData?.active_branch_id]
  );

  const formik = useFormik({
    initialValues: {
      name: "",
      category_kind: "other",
      is_withdrawable: 0,
      company_bank_id: "",
      threshold: "",
      end_date: "",
      start_date: "",
    },
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Contribution name is required"),
      company_bank_id: Yup.string().required("Payment bank is required"),
      category_kind: Yup.string()
        .oneOf(["savings", "shares", "other"], "Invalid category type")
        .required("Category type is required"),
      end_date: Yup.date()
        .nullable()
        .test(
          "future-date",
          "End date must be greater than today",
          function (value) {
            if (!value) return true;
            return moment(value).isAfter(moment(), "day");
          }
        ),
      is_withdrawable: Yup.number()
        .nullable()
        .oneOf([0, 1], "Invalid withdrawable status"),
      threshold: Yup.number().nullable().typeError("Threshold must be a number"),
      start_date: Yup.date().nullable(),
    }),
  });
  const [createContributionCategory, { isLoading }] =
    useCreateContributionCategoryMutation();
  const { data } = useGetAllBankInfoQuery({ pageNumber: 1 });
  const formattedCompanyBanks = data?.company_banks?.map((item) => {
    return {
      label: item.bank_name + " | " + item.account_name,
      value: item.bank_id,
    };
  });

  const kindLocksWithdrawable =
    formik.values.category_kind === "savings" ||
    formik.values.category_kind === "shares";

  const handleKindChange = (option) => {
    const kind = option?.value || "other";
    formik.setFieldValue("category_kind", kind);
    formik.setFieldTouched("category_kind", true);
    const forced = withdrawableForKind(kind);
    if (forced !== null) {
      formik.setFieldValue("is_withdrawable", forced);
    } else if (
      formik.values.is_withdrawable === "" ||
      formik.values.is_withdrawable == null
    ) {
      formik.setFieldValue("is_withdrawable", 0);
    }
  };

  const handleFormSubmit = async (formikValues) => {
    if (!activeBranchId) {
      return toast.error(
        "No active branch found. Select an active branch and try again."
      );
    }

    const formattedEndDate = formikValues.end_date
      ? moment(formikValues.end_date).format("YYYY-MM-DD")
      : null;

    const kind = formikValues.category_kind || "other";
    const forced = withdrawableForKind(kind);

    const updatedValues = {
      name: formikValues.name,
      company_bank_id: parseInt(formikValues.company_bank_id),
      branches: [{ branch_id: Number(activeBranchId) }],
      category_kind: kind,
      is_withdrawable:
        forced !== null
          ? forced
          : parseInt(formikValues.is_withdrawable ?? 0, 10),
    };

    const formattedStartDate = formikValues.start_date
      ? moment(formikValues.start_date).format("YYYY-MM-DD")
      : null;

    if (formattedStartDate) {
      updatedValues.start_date = formattedStartDate;
    }

    if (formattedEndDate) {
      updatedValues.end_date = formattedEndDate;
    }

    const response = await createContributionCategory(updatedValues);
    if ("error" in response) {
      const { data } = response.error;
      return toast.error(data.message || "Failed to create contribution");
    }
    const { message, status } = response.data;
    if (!status) return toast.error(message);
    toast.success(message);
    formik.resetForm();
    if (handleClose) {
      handleClose();
    } else {
      navigate(`/super_admin/contribution/contribution_history`);
    }
  };

  return (
    <div>
      <h4 className="mb-0 text-xl py-3 text-black1 fw-bold text-center">
        Create New Contribution
      </h4>
      {currentBranch?.name && (
        <p className="text-center text-sm text-blacklight px-3 mb-2">
          This category will be created for{" "}
          <span className="fw-semibold text-black1">{currentBranch.name}</span>{" "}
          (active branch).
        </p>
      )}
      <form className="w-full" onSubmit={formik.handleSubmit}>
        <section className="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-10">
          <Input
            type="text"
            label="Contribution name"
            placeholder={"Input contribution name"}
            name="name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
            error={formik?.touched.name && formik.errors.name}
            errorMessage={formik.errors.name}
          />

          <Input
            type="number"
            label="Contribution threshold"
            placeholder={"#1,000,000"}
            name="threshold"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.threshold}
            error={formik?.touched.threshold && formik.errors.threshold}
            errorMessage={formik.errors.threshold}
          />
          <Input
            type="date"
            label="Start date"
            placeholder={"Select start date"}
            name="start_date"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.start_date}
            error={formik?.touched.start_date && formik.errors.start_date}
            errorMessage={formik.errors.start_date}
          />
          <Input
            type="date"
            label="End date"
            placeholder={"Select end date"}
            name="end_date"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.end_date}
            error={formik?.touched.end_date && formik.errors.end_date}
            errorMessage={formik.errors.end_date}
          />

          <div>
            <CustomSelect
              options={formattedCompanyBanks}
              styles={customStyles}
              label={"Payment bank"}
              isLoading={!data}
              noOptionsMessage={() =>
                !data ? "Loading banks..." : "No banks available"
              }
              onChange={(value) => {
                formik.setFieldValue("company_bank_id", value?.value || "");
                formik.setFieldTouched("company_bank_id", true);
              }}
              value={
                formattedCompanyBanks?.find(
                  (bank) =>
                    String(bank.value) === String(formik.values.company_bank_id)
                ) || null
              }
              placeholder="Select bank to accept contribution"
            />
            {formik?.touched.company_bank_id && formik.errors.company_bank_id && (
              <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                {formik.errors.company_bank_id}
              </span>
            )}
          </div>
          <div>
            <CustomSelect
              options={CATEGORY_KIND_OPTIONS}
              styles={customStyles}
              label={"Category type"}
              onChange={handleKindChange}
              value={
                CATEGORY_KIND_OPTIONS.find(
                  (o) => o.value === formik.values.category_kind
                ) || CATEGORY_KIND_OPTIONS[2]
              }
              placeholder="Select category type"
            />
            {formik?.touched.category_kind && formik.errors.category_kind && (
              <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                {formik.errors.category_kind}
              </span>
            )}
          </div>
          <div>
            <CustomSelect
              options={[
                {
                  label: "Withdrawable",
                  value: 1,
                },
                {
                  label: "Non-Withdrawable",
                  value: 0,
                },
              ]}
              styles={customStyles}
              label={"Withdrawable status"}
              isDisabled={kindLocksWithdrawable}
              onChange={(value) => {
                if (kindLocksWithdrawable) return;
                formik.setFieldValue("is_withdrawable", value?.value ?? "");
                formik.setFieldTouched("is_withdrawable", true);
              }}
              value={
                formik.values.is_withdrawable === "" ||
                formik.values.is_withdrawable == null
                  ? 0
                  : formik.values.is_withdrawable
              }
              placeholder="Select withdrawable status"
            />
            {kindLocksWithdrawable && (
              <span className="text-xs text-muted mt-1 d-block">
                Withdrawable status is set by category type (savings /
                shares).
              </span>
            )}
            {formik?.touched.is_withdrawable &&
              formik.errors.is_withdrawable && (
                <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                  {formik.errors.is_withdrawable}
                </span>
              )}
          </div>
        </section>
        <div className="my-3 w-full flex justify-center ">
          <button
            type="submit"
            className="w-[200px] h-[50px] rounded-[10px] bg-new-primary text-white"
          >
            {isLoading ? "creating category..." : "Create category"}
          </button>
        </div>
      </form>
    </div>
  );
}
